import { RunType } from '@/enums/run-type.enum';
import { useWorkflowStore } from '@/state/workflow.state';
import { assetUrl } from '@/utils/assetUrls';
import { validateFile } from '@/utils/nodeInputValidation';
import { uploadAssetFile } from '@/utils/uploadAssetFile';
import { decodeClampedImage } from '@/components/Recipe/resourceLoading';
import { CompositorAiError, CompositorAiErrorCode } from './compositorAiError';
import type { FileKind } from 'web';

type LayerImage = Extract<FileKind, { type: 'image' }>;

/** Pixel bounds measured from the top-left of the provider's full output canvas. */
interface LayerPlacement {
  x: number;
  y: number;
  width: number;
  height: number;
  canvasWidth: number;
  canvasHeight: number;
}

function getPlacement(result: object): LayerPlacement | undefined {
  if (!('placement' in result) || result.placement == null) return undefined;
  const placement = result.placement as LayerPlacement;
  if (
    typeof placement !== 'object' ||
    ![placement.x, placement.y, placement.width, placement.height, placement.canvasWidth, placement.canvasHeight].every(
      (value) => typeof value === 'number' && Number.isFinite(value),
    ) ||
    placement.width <= 0 ||
    placement.height <= 0 ||
    placement.canvasWidth <= 0 ||
    placement.canvasHeight <= 0
  ) {
    throw new CompositorAiError(CompositorAiErrorCode.InvalidResult);
  }
  return placement;
}

async function loadImage(file: LayerImage, signal?: AbortSignal): Promise<ImageBitmap> {
  signal?.throwIfAborted();
  const url = assetUrl(file, useWorkflowStore.getState().resolvedAssets);
  if (!url) throw new CompositorAiError(CompositorAiErrorCode.InvalidResult);
  // Use the existing asset loader without downsampling: bounds are in provider canvas pixels.
  const { bitmap } = await decodeClampedImage(url, Number.POSITIVE_INFINITY);
  if (signal?.aborted) {
    bitmap.close();
    signal.throwIfAborted();
  }
  return bitmap;
}

/**
 * Restore cropped layers to transparent full-canvas PNGs before the aligned-layer import.
 * Results must have unique, nonnegative integer zIndex values, with a full-canvas background at 0.
 * Cropped layers require placement bounds in that canvas's pixel coordinates.
 * Provider adapters must normalize their output to this format before calling this utility.
 */
export async function prepareCroppedLayerImages(results: unknown[], signal?: AbortSignal): Promise<LayerImage[]> {
  const ordered = results
    .map((result, index) => {
      const file = validateFile(result);
      if (
        !result ||
        typeof result !== 'object' ||
        file?.type !== 'image' ||
        !(file.width > 0 && file.height > 0) ||
        !Number.isFinite(file.width + file.height)
      ) {
        throw new CompositorAiError(CompositorAiErrorCode.InvalidResult, `Layer ${index} is not a valid image`);
      }
      if (
        !('zIndex' in result) ||
        typeof result.zIndex !== 'number' ||
        !Number.isInteger(result.zIndex) ||
        result.zIndex < 0
      ) {
        throw new CompositorAiError(
          CompositorAiErrorCode.InvalidResult,
          `Layer ${index} is missing a valid zIndex in the prediction result`,
        );
      }
      // validateFile keeps asset fields only; read placement and stacking metadata from the result.
      return { file, zIndex: result.zIndex, placement: getPlacement(result) };
    })
    .sort((a, b) => a.zIndex - b.zIndex);
  if (ordered[0]?.zIndex !== 0 || new Set(ordered.map((layer) => layer.zIndex)).size !== ordered.length) {
    throw new CompositorAiError(CompositorAiErrorCode.InvalidResult);
  }

  const base = await loadImage(ordered[0].file, signal);
  const { width, height } = base;
  const canvas = document.createElement('canvas');
  try {
    if (!(width > 0 && height > 0) || width * height > 36_000_000) {
      throw new CompositorAiError(CompositorAiErrorCode.InvalidResult);
    }
    // Validate every crop before uploading anything. Missing metadata must not stretch objects.
    for (const { file, placement, zIndex } of ordered) {
      if (
        (file.width !== width || file.height !== height) &&
        (!placement || placement.canvasWidth !== width || placement.canvasHeight !== height)
      ) {
        throw new CompositorAiError(
          CompositorAiErrorCode.InvalidResult,
          `Layer ${zIndex} (${file.width}x${file.height}) is missing placement for the ${width}x${height} canvas`,
        );
      }
    }
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) throw new CompositorAiError(CompositorAiErrorCode.InvalidResult);
    const images: LayerImage[] = [];
    for (const [index, { file, placement }] of ordered.entries()) {
      signal?.throwIfAborted();
      const image = index === 0 ? base : await loadImage(file, signal);
      try {
        context.clearRect(0, 0, width, height);
        if (image.width === width && image.height === height) {
          context.drawImage(image, 0, 0);
        } else {
          if (!placement || placement.canvasWidth !== width || placement.canvasHeight !== height) {
            throw new CompositorAiError(CompositorAiErrorCode.InvalidResult);
          }
          context.drawImage(image, placement.x, placement.y, placement.width, placement.height);
        }
      } finally {
        if (image !== base) image.close();
      }
      const blob = await new Promise<Blob>((resolve, reject) =>
        canvas.toBlob(
          (result) => (result ? resolve(result) : reject(new CompositorAiError(CompositorAiErrorCode.InvalidResult))),
          'image/png',
        ),
      );
      signal?.throwIfAborted();
      const asset = await uploadAssetFile(
        new File([blob], `layer-${index}.png`, { type: 'image/png' }),
        RunType.WorkflowEditor,
      );
      const uploaded = validateFile(asset);
      if (uploaded?.type !== 'image') throw new CompositorAiError(CompositorAiErrorCode.InvalidResult);
      images.push({ ...uploaded, name: file.name?.trim() || undefined });
    }
    return images;
  } finally {
    base.close();
    canvas.width = canvas.height = 0;
  }
}
